import DonutChart from '@/components/DonutChart'
import { DATE_TIME_FORMAT } from '@/libs/core/constants'
import fieldToSortStr from '@/libs/utils/fieldToSortStr'
import Button from '@/shared/Button'
import Table from '@/shared/Table'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import get from 'lodash/get'
import round from 'lodash/round'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

const mapStateToProps = ({ campaign, auth, loading }) => ({
  account: auth.account,
  campaignDetails: campaign.campaignDetails,
  isUsersLoading: loading['campaign/getCampaignUsers'],
  isRemindersLoading: loading['campaign/sendCampaignReminders'],
})

const fieldMap = {
  userName: 'USER_NAME',
  userEmail: 'USER_EMAIL',
  complePercent: 'USER_COMPL',
  status: 'USER_STATUS',
  sendNotiTime: 'USER_NOTITIME',
  lastReadTime: 'USER_LASTTIME',
}

const TextItem = ({ label, labelProps, value, valueProps, suffix, suffixProps }) => (
  <Grid container spacing={1}>
    <Grid item {...labelProps}>{`${label}:`}</Grid>
    <Grid item {...valueProps}>
      {value}
    </Grid>
    {suffix && (
      <Grid item {...suffixProps}>
        {suffix}
      </Grid>
    )}
  </Grid>
)

const CardItem = ({ title, cardContent, children }) => (
  <Card
    sx={{
      mt: 2,
      background: 'rgba(61, 133, 240, 0.1)',
      color: theme => theme.palette.secondary[80].main,
    }}
  >
    <CardContent>
      <Typography>{title}</Typography>
      <Box padding={1}>{cardContent}</Box>
    </CardContent>
    {children}
  </Card>
)

const UserCountItem = ({ label, value, tooltip }) => (
  <TextItem
    label={label}
    value={value}
    valueProps={{ sx: { flex: 1, borderBottom: '2px solid #00000040' } }}
    suffix={
      <Tooltip title={tooltip} placement="top-end">
        <HelpOutlineOutlinedIcon htmlColor="black" />
      </Tooltip>
    }
  />
)

const CampaignDetails = ({
  dispatch,
  campaignDetails,
  account,
  isUsersLoading,
  isRemindersLoading,
}) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [sort, setSort] = useState([{ field: 'userName', sort: 'asc' }])
  const [selectedUsers, setSelectedUsers] = useState([])

  const { accountId } = account || {}

  const {
    trainingCampName,
    trainingCampId,
    initialTime,
    endTime,
    remainDays,
    status,
    finishedUsers,
    inProgress,
    notStartUsers,
    totalCourseNum,
    totalUsers = 0,
    users,
    undeliveredUsers = 0,
  } = campaignDetails || {}

  const { paging: usersPagination, data: usersData = [] } = users || {}

  const pagination = {
    ...usersPagination,
    page,
    pageSize,
    onPageChange: setPage,
    onPageSizeChange: setPageSize,
  }

  const cols = [
    { field: 'userName', headerName: t('general.name'), width: 200 },
    {
      field: 'userEmail',
      headerName: t('general.email'),
      width: 200,
      renderCell: ({ value }) => (
        <Tooltip title={value} placement="top-end">
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</span>
        </Tooltip>
      ),
    },
    {
      field: 'complePercent',
      headerName: `${t('general.completion_rate')}%`,
      width: 120,
    },
    {
      field: 'status',
      headerName: t('general.status'),
      width: 100,
      valueFormatter: ({ value }) => t(`status.${value}`),
    },
    {
      field: 'sendNotiTime',
      headerName: t('general.notified_time'),
      width: 200,
      valueFormatter: ({ value }) => value && moment(value).format(DATE_TIME_FORMAT),
    },
    {
      field: 'lastReadTime',
      headerName: t('general.last_read_time'),
      width: 200,
      valueFormatter: ({ value }) => value && moment(value).format(DATE_TIME_FORMAT),
    },
  ]

  const chartsData =
    finishedUsers + notStartUsers + inProgress === 0
      ? [{ value: 1, fill: '#D9D9D9' }]
      : [
          { name: t('general.completed'), value: finishedUsers, fill: '#FF9B0BB8' },
          { name: t('general.pending'), value: notStartUsers, fill: '#FF6600DE' },
          { name: t('general.inprogress'), value: inProgress, fill: '#FF9B0B66' },
        ]

  const sendReminders = () => {
    dispatch({
      type: 'campaign/sendCampaignReminders',
      payload: {
        campaignId: trainingCampId,
        data: { accountId, users: selectedUsers, sendAllThisCamp: false },
      },
      successCallback: () => {
        dispatch({
          type: 'app/notify',
          payload: { message: t('messages.reminders_sent'), severity: 'success' },
        })
        setSelectedUsers([])
      },
    })
  }

  const generateLegendsText = value => {
    const percent = round((value / (totalUsers || 1)) * 100)

    return `${percent}% (${value}/${totalUsers})`
  }

  useEffect(() => {
    if (trainingCampId && accountId) {
      dispatch({
        type: 'campaign/getCampaignUsers',
        payload: {
          campaignId: trainingCampId,
          params: {
            accountId,
            pageIndex: page,
            pageSize,
            sort: fieldToSortStr(get(sort, '[0]'), fieldMap),
          },
        },
      })
    }
  }, [dispatch, trainingCampId, accountId, page, pageSize, sort])

  return (
    <Container
      sx={{
        color: theme => theme.palette.secondary[80].main,
      }}
    >
      <Typography variant="h5">{trainingCampName}</Typography>
      <CardItem
        title={t('general.basic_info')}
        cardContent={
          <Grid container spacing={1}>
            <Grid container item xs={12} sm={6} spacing={1}>
              <Grid item xs={12}>
                <TextItem
                  label={t('general.start')}
                  value={moment(initialTime).format(DATE_TIME_FORMAT)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextItem
                  label={t('general.end')}
                  value={moment(endTime).format(DATE_TIME_FORMAT)}
                />
              </Grid>
            </Grid>

            <Grid container item xs={12} sm={6} spacing={1}>
              <Grid item xs={12}>
                <TextItem label={t('general.remaining_days')} value={remainDays} />
              </Grid>
              <Grid item xs={12}>
                <TextItem label={t('general.campaign_status')} value={t(`status.${status}`)} />
              </Grid>
            </Grid>
          </Grid>
        }
      />

      <CardItem
        title={t('general.course_info')}
        cardContent={
          <Grid container spacing={1}>
            <Grid container item xs={12} sm={6} spacing={1}>
              <Grid item xs={12}>
                <TextItem
                  label={t('general.sent_head_count')}
                  value={totalUsers - undeliveredUsers}
                />
              </Grid>
              <Grid item xs={12}>
                <TextItem label={t('general.unsent_head_count')} value={undeliveredUsers} />
              </Grid>
            </Grid>

            <Grid container item xs={12} sm={6} spacing={1}>
              <Grid item xs={12}>
                <TextItem label={t('general.attended_head_count')} value={finishedUsers} />
              </Grid>
              <Grid item xs={12}>
                <TextItem label={t('general.unattended_head_count')} value={notStartUsers} />
              </Grid>
            </Grid>

            <Grid container item xs={12}>
              <TextItem label={t('general.complete_course_count')} value={totalCourseNum} />
            </Grid>
          </Grid>
        }
      />

      <CardItem
        title={t('general.statistics')}
        cardContent={
          <Grid container spacing={1}>
            <Grid container item xs={12} sm={6}>
              <DonutChart dataKey="value" data={chartsData} />
            </Grid>
            <Grid container item xs={12} sm={6} spacing={1}>
              <Grid item xs={12}>
                <UserCountItem
                  label={t('general.completed')}
                  value={generateLegendsText(finishedUsers)}
                  tooltip={t('tooltips.completed_users')}
                />
              </Grid>
              <Grid item xs={12}>
                <UserCountItem
                  label={t('general.inprogress')}
                  value={generateLegendsText(inProgress)}
                  tooltip={t('tooltips.in_progress_users')}
                />
              </Grid>
              <Grid item xs={12}>
                <UserCountItem
                  label={t('general.pending')}
                  value={generateLegendsText(notStartUsers)}
                  tooltip={t('tooltips.not_started_users')}
                />
              </Grid>
              <Grid item xs={12}>
                <UserCountItem
                  label={t('general.unsent')}
                  value={generateLegendsText(undeliveredUsers)}
                  tooltip={t('tooltips.not_sent_users')}
                />
              </Grid>
            </Grid>
          </Grid>
        }
      />

      <CardItem title={t('general.inprogress')}>
        <Table
          minHeight={300}
          columns={cols}
          rows={usersData}
          dataGridConfig={{
            sortingMode: 'server',
            sortingOrder: ['desc', 'asc', null],
            sortModel: sort,
            onSortModelChange: newSort => setSort(newSort),
            borderColor: theme => theme.palette.other.black.main,
            checkboxSelection: ['NS', 'IP'].includes(status),
            getRowId: row => row.userId,
            loading: isUsersLoading,
            onSelectionModelChange: model => {
              setSelectedUsers(model)
            },
            selectionModel: selectedUsers,
            isRowSelectable: params => params.row.status !== 'COM',
          }}
          pagination={pagination}
        />
        {['NS', 'IP'].includes(status) && (
          <CardContent>
            <Stack direction="row">
              <Box flex={1} />
              <Button
                disabled={selectedUsers.length === 0 || isRemindersLoading}
                loading={isRemindersLoading}
                color="btn_positive"
                onClick={() => {
                  sendReminders()
                }}
              >
                {t('general.send_course_reminder')}
              </Button>
            </Stack>
          </CardContent>
        )}
      </CardItem>
    </Container>
  )
}

export default connect(mapStateToProps)(CampaignDetails)
